import React, { useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import cn from "classnames";
import FlipHorizontalIcon from "./Icons/FlipHorizontalIcon";
import RotateRightIcon from "./Icons/RotateRightIcon";
import RotateComponent from "./RotateComponent";
import RotateLeftIcon from "./Icons/RotateLeftIcon";
import FlipVerticalIcon from "./Icons/FlipVerticalIcon";


const Navigation = forwardRef(
  (
    {
      value = 0,
      onRotate,
      onRotateEnd,
      onFlip,
      disabled = false,
      className = "",
      buttonClassName = "",
      rotateComponentClassName = "",
      barClassName = "",
      highlightedBarClassName = "",
      zeroBarClassName = "",
      valueBarClassName = "",
    },
    ref
  ) => {
    const rotateRef = useRef(null);

    const adjustmentAngle = useMemo(() => {
        const nearestRightAngle = Math.round(value / 90) * 90;
        return value - nearestRightAngle;
    }, [value]);

    // expose refresh to parent
    useImperativeHandle(ref, () => ({
        refresh: () => {
            if (rotateRef.current && rotateRef.current.refresh) {
                rotateRef.current.refresh();
            }
        },
    }));

    // 🔥 ACTIONS

    const rotateTo = (angle) => {
        if (!onRotate || disabled) return;

        onRotate(angle, {
            transitions: false,
            interaction: true,
            immediately: true,
        });
    };

    const rotateLeft = () => {
        if (!onRotate || disabled) return;

        if (adjustmentAngle > 0) {
            onRotate(-adjustmentAngle);
        } else if (adjustmentAngle < 0) {
            onRotate(-90 - adjustmentAngle);
        } else {
            onRotate(-90);
        }
    };

    const rotateRight = () => {
        if (!onRotate || disabled) return;

        if (adjustmentAngle > 0) {
            onRotate(90 - adjustmentAngle);
        } else if (adjustmentAngle < 0) {
            onRotate(-adjustmentAngle);
        } else {
            onRotate(90);
        }
    };

    const flipHorizontal = () => {
      if (onFlip && !disabled) onFlip(true);
    };

    const flipVertical = () => {
      if (onFlip && !disabled) onFlip(false, true);
    };

    return (
      <div className={cn("rmc-navigation", className)}>
        {/* Flip Horizontal */}
        <button
          type="button"
          className={cn("rmc-navigation__button", buttonClassName)}
          onClick={flipHorizontal}
          disabled={disabled}
        >
          <FlipHorizontalIcon />
        </button>

        {/* Rotate Right */}
        <button
          type="button"
          className={cn("rmc-navigation__button", buttonClassName)}
          onClick={rotateRight}
          disabled={disabled}
        >
          <RotateRightIcon />
        </button>

        {/* Rotate Slider */}
        <RotateComponent
          ref={rotateRef}
          className={cn("rmc-navigation__rotator", rotateComponentClassName)}
          barClassName={barClassName}
          zeroBarClassName={zeroBarClassName}
          valueBarClassName={valueBarClassName}
          highlightedBarClassName={highlightedBarClassName}
          from={-45}
          to={45}
          precision={0.1}
          value={adjustmentAngle}
          onChange={rotateTo}
          onBlur={onRotateEnd}
        />

        {/* Rotate Left */}
        <button
          type="button"
          className={cn("rmc-navigation__button", buttonClassName)}
          onClick={rotateLeft}
          disabled={disabled}
        >
          <RotateLeftIcon />
        </button>

        {/* Flip Vertical */}
        <button
          type="button"
          className={cn("rmc-navigation__button", buttonClassName)}
          onClick={flipVertical}
          disabled={disabled}
        >
          <FlipVerticalIcon />
        </button>
      </div>
    );
  }
);

export default Navigation;